import React from "react";
import { DownloadIconButton, ViewIconButton } from "components/DatatablesActionIcons";
import ApprovedProofOrderSummaryView from "components/ApprovedProofOrderSummaryView";
import ProjectFilePreviewModal from "components/ProjectFilePreviewModal";
import { AppIcon } from "components/AppIcon";

type AddressLike = {
  first_name?: string;
  last_name?: string;
  address_details?: {
    addressLine1?: string;
    address_line_1?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    zip?: string;
    phone?: string;
  };
};

function formatName(addr?: AddressLike | null): string {
  const first = String(addr?.first_name || "").trim();
  const last = String(addr?.last_name || "").trim();
  return [first, last].filter(Boolean).join(" ").trim() || "—";
}

function formatPhone(addr?: AddressLike | null): string {
  return String(addr?.address_details?.phone || "").trim() || "—";
}

function formatAddress(addr?: AddressLike | null): string {
  const d = addr?.address_details || {};
  const line1 = String(d.address_line_1 || d.addressLine1 || "").trim();
  const city = String(d.city || "").trim();
  const state = String(d.state || d.stateCode || "").trim();
  const zip = String(d.zip || "").trim();
  return [line1, [city, state].filter(Boolean).join(", "), zip].filter(Boolean).join(" ").trim() || "—";
}

type ProofFile = {
  id: string;
  name: string;
  href?: string;
  previewUrl?: string;
  fileId?: string;
  quantity?: number;
  sizeLabel?: string;
  dateLabel?: string;
};

export interface CompactApprovedProofCardProps {
  version: number;
  files: ProofFile[];
  approvedBy?: string;
  approvedAt?: string;
  footerBy?: string;
  shippingName?: string;
  shippingDesc?: string;
  shippingAmountLabel?: string;
  /** Sales (default) keeps compact shipping row; installation shows billing + installation addresses. */
  variant?: "sales" | "installation";
  billingAddress?: AddressLike | null;
  installationAddress?: AddressLike | null;
  onDownloadByFileId?: (fileId: string, fileName: string) => void | Promise<void>;
  onViewFile?: (file: ProofFile) => void;
  onDownloadFile?: (file: ProofFile) => void;
  /** When true, show read-only SUMMARY block below approved proof files. */
  showOrderSummary?: boolean;
  orderSummary?: string;
}

export default function CompactApprovedProofCard({
  version,
  files,
  approvedBy,
  approvedAt,
  footerBy,
  shippingName,
  shippingDesc,
  shippingAmountLabel,
  variant = "sales",
  billingAddress,
  installationAddress,
  onDownloadByFileId,
  onViewFile,
  onDownloadFile,
  showOrderSummary = false,
  orderSummary,
}: CompactApprovedProofCardProps) {
  const assignedName = String(footerBy || "").trim();
  const approvedByName = String(approvedBy || "").trim();
  const fileCount = files.length;
  const isInstallation = variant === "installation";
  const [preview, setPreview] = React.useState<{
    name: string;
    previewUrl: string;
    href: string;
    sizeLabel?: string;
    dateLabel?: string;
  } | null>(null);

  const defaultView = React.useCallback((file: ProofFile) => {
    const href = String(file.href || "").trim();
    const previewUrl = String(file.previewUrl || "").trim();
    const effectiveHref = href || previewUrl;
    if (!effectiveHref) return;
    setPreview({
      name: file.name,
      previewUrl: previewUrl || effectiveHref,
      href: effectiveHref,
      sizeLabel: file.sizeLabel || "—",
      dateLabel: file.dateLabel || "—",
    });
  }, []);

  const defaultDownload = React.useCallback(
    (file: ProofFile) => {
      const fileId = String(file.fileId || "").trim();
      if (fileId && onDownloadByFileId) {
        void onDownloadByFileId(fileId, file.name);
        return;
      }
      const href = String(file.href || file.previewUrl || "").trim();
      if (!href) return;
      const link = document.createElement("a");
      link.href = href;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    [onDownloadByFileId]
  );

  return (
    <div
      style={{
        border: "1px solid #22c55e",
        borderRadius: 10,
        overflow: "hidden",
        marginTop: 5,
        marginBottom: 10,
        background: "#ffffff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "9px 14px",
          background: "#f0fdf4",
          borderBottom: "1px solid #bbf7d0",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap" }}>
          <AppIcon name="proof" size={22} alt={`Proof V${version}`} />
          <span style={{ fontWeight: 500, color: "#111827" }}>Proof V{version}</span>
          <span style={{ color: "#4b5563", fontSize: 14 }}>
            {fileCount} file{fileCount !== 1 ? "s" : ""}
          </span>
          <span
            style={{
              background: "#367C2B",
              color: "#fff",
              borderRadius: 999,
              padding: "3px 10px",
              fontWeight: 700,
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {/* <i className="ri-checkbox-circle-fill" style={{ fontSize: 13, lineHeight: 1 }} /> */}
            Approved
          </span>
        </div>
        {approvedByName ? (
          <div style={{ color: "#15803d", fontWeight: 500, whiteSpace: "nowrap", fontSize: 14 }}>
            by {approvedByName}
          </div>
        ) : null}
      </div>

      {files.map((file, index) => {
        const letter = String.fromCharCode(65 + (index % 26));
        return (
          <div
            key={file.id || `${file.name}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: "1px solid #e5e7eb",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#f3f4f6",
                  color: "#6b7280",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="ri-file-text-line" style={{ fontSize: 15, lineHeight: 1 }} />
              </span>
              <span style={{ color: "#6b7280", fontWeight: 700, fontSize: 12, letterSpacing: "0.03em" }}>
                FILE {letter}
              </span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#111827" }}>{file.name}</span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {Number.isFinite(Number(file.quantity)) && Number(file.quantity) > 0 ? (
                <span
                  style={{
                    background: "#dcfce7",
                    borderRadius: 999,
                    padding: "4px 8px 4px 4px",
                    fontSize: 12,
                    lineHeight: 1.1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      background: "#16a34a",
                      color: "#ffffff",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                    }}
                  >
                    {Math.floor(Number(file.quantity))}
                  </span>
                  <span style={{ color: "#166534", fontWeight: 600 }}>
                    {Math.floor(Number(file.quantity)) === 1 ? "unit" : "units"}
                  </span>
                </span>
              ) : null}
              <ViewIconButton
                type="button"
                title="View"
                ariaLabel="View"
                size={30}
                onClick={() => (onViewFile ? onViewFile(file) : defaultView(file))}
              />
              <DownloadIconButton
                type="button"
                title="Download"
                ariaLabel="Download"
                size={30}
                onClick={() => (onDownloadFile ? onDownloadFile(file) : defaultDownload(file))}
              />
            </div>
          </div>
        );
      })}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isInstallation ? "1fr 1fr 1fr" : "1fr 1fr",
          borderBottom: showOrderSummary ? "0" : "1px solid #e5e7eb",
        }}
      >
        {isInstallation ? (
          <>
            <div style={{ padding: "10px 14px", borderRight: "1px solid #e5e7eb", minHeight: 74 }}>
              <div style={{ color: "#2563eb", fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", marginBottom: 6 }}>
                BILLING
              </div>
              <div style={{ fontWeight: 700, color: "#111827" }}>{formatName(billingAddress)}</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>{formatPhone(billingAddress)}</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>{formatAddress(billingAddress)}</div>
            </div>
            <div style={{ padding: "10px 14px", borderRight: "1px solid #e5e7eb", minHeight: 74 }}>
              <div style={{ color: "#2563eb", fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", marginBottom: 6 }}>
                INSTALLATION
              </div>
              <div style={{ fontWeight: 700, color: "#111827" }}>{formatName(installationAddress)}</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>{formatPhone(installationAddress)}</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>{formatAddress(installationAddress)}</div>
            </div>
          </>
        ) : (
          <div style={{ padding: "10px 14px", borderRight: "1px solid #e5e7eb", minHeight: 74 }}>
            <div style={{ visibility: "hidden" }}>
              <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 13, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 6 }}>
                <i className="ri-menu-2-line" style={{ fontSize: 13 }} /> QTY
              </div>
              <div style={{ fontSize: 20, lineHeight: 1.2, color: "#111827", marginTop: 8, fontWeight: 500 }}>0</div>
            </div>
          </div>
        )}
        <div style={{ padding: "10px 14px", minHeight: 74 }}>
          <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 13, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 6 }}>
            <i className="ri-arrow-right-line" style={{ fontSize: 13 }} /> SHIPPING
          </div>
          <div style={{ fontWeight: 500, color: "#111827", marginTop: 8 }}>{shippingName || "—"}</div>
          <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 2 }}>{shippingDesc || "—"}</div>
          <div style={{ color: "#16a34a", fontWeight: 700, marginTop: 2 }}>{shippingAmountLabel || ""}</div>
        </div>
      </div>

      {showOrderSummary ? <ApprovedProofOrderSummaryView orderSummary={orderSummary} /> : null}

      <div style={{ padding: "9px 14px", color: "#64748b", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
        {assignedName ? (
          <>
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: 999,
                background: "#d1d5dc",
                color: "#6b7280",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="ri-user-line" style={{ fontSize: 12, lineHeight: 1 }} />
            </span>
            <span style={{ color: "#374151", fontWeight: 500 }}>{assignedName}</span>
          </>
        ) : null}
        {approvedAt ? <span>{approvedAt}</span> : null}
      </div>
      <ProjectFilePreviewModal
        data={preview}
        onClose={() => setPreview(null)}
        onDownload={() => {
          if (!preview) return;
          const link = document.createElement("a");
          link.href = preview.href;
          link.download = preview.name;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }}
      />
    </div>
  );
}
